import { z } from 'zod';
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, 'Expected a real calendar date');
const text = z.string().nullable();
export const allocationResignationPreviewInputSchema = z.object({
    confirmation: z.object({
        date,
        confirmedFinal: z.literal(true),
        evidenceReference: z.string().trim().min(1).max(1000),
        approvedBy: z.string().trim().min(1).max(200),
    }).strict().optional(),
    selection: z.object({
        founderId: z.string().uuid().optional(),
        assignmentId: z.string().uuid().optional(),
        requestId: z.string().uuid().optional(),
    }).strict().optional(),
}).strict();
export const allocationResignationPreviewEvidenceSchema = z.object({
    doer: z.object({
        id: z.string(), doer_code: z.string(), first_name: z.string(), last_name: z.string(),
        founder_id_ref: text, founder_id_ref_2: text, doer_type: text,
        category: text, section: text, matching_status: text, employee_status: text, availability_status: text, quick_notes: text,
        end_date: text, last_day_ptfd1: text, last_day_ptfd2: text, updated_at: z.string(),
    }),
    resignations: z.array(z.object({
        id: z.string(), doer_id: text, submitted_date: z.string(),
        last_working_date: text, status: text, offboarding_notes: text, updated_at: z.string(),
    })),
    founders: z.array(z.object({
        id: z.string(), external_id: text, founder_code: text, first_name: text, last_name: text,
    })),
    assignments: z.array(z.object({
        id: z.string(), doer_id: z.string(), founder_id: text, assignment_type: text,
        status: z.string(), start_date: text, end_date: text,
    })),
    requests: z.array(z.object({
        id: z.string(), founder_id: text, current_doer_id: text, current_doer: text,
        request_type: z.string(), status: z.string(), last_day_of_work: text,
        cancellation_date: text, cx_allocation_notes: text, notes: text,
        created_at: z.string(), updated_at: z.string(),
    })),
    truncated: z.boolean(),
});
export const allocationResignationPreviewResponseSchema = z.object({
    readOnly: z.literal(true),
    generatedAt: z.string().datetime(),
    state: z.enum(['needs_confirmation', 'needs_review', 'ready_for_review']),
    confirmation: allocationResignationPreviewInputSchema.shape.confirmation.unwrap().extend({
        suppliedBy: z.string(),
        /**
         * How the final date reached us, and the distinction matters.
         *
         * `operator_attestation` — a person typed the date and referenced their
         * evidence. Nothing here verified it.
         * `source_read` — this process read the approved Offboarding submission
         * itself through the authenticated reader.
         *
         * Deliberately NOT part of the request schema. A caller cannot declare its
         * own input provider-read; only the code path that actually performed the
         * read passes the second value.
         */
        verification: z.enum(['operator_attestation', 'source_read']),
    }).nullable(),
    comparison: z.enum(['not_compared', 'equal', 'different', 'no_request']),
    relationship: z.object({
        // Null when the profile link has no assignment row behind it (a backfill
        // gap, listed as `assignment_row_missing`; 13 Oct 2026).
        founderId: z.string(), assignmentId: z.string().nullable(), slot: z.enum(['FT', 'PTFD1', 'PTFD2']),
        requestId: z.string().nullable(), endDate: date.nullable(),
    }).nullable(),
    /**
     * `Archived` (with matching status `Resigned` and category `Archive`) is the
     * archive movement: an unmatched doer whose approved last day of work is
     * today or has passed (Allocation, 2026-10-07).
     */
    classificationProposal: z.enum(['Resigning Doers', 'Resigning Doers - Update Offboarding Date', 'Archived']).nullable(),
    matchingStatusProposal: z.enum(['Resigning - Matched', 'Resigning - Unmatched', 'Resigned']).nullable(),
    /** Set only by the archive movement; every other proposal leaves the category alone. */
    categoryProposal: z.enum(['Archive']).nullable(),
    noteProposal: z.object({ proposedQuickNotes: z.string(), changed: z.boolean() }).nullable(),
    findings: z.array(z.object({
        code: z.string(), severity: z.enum(['review', 'info']), detail: z.string(),
    })),
    evidence: allocationResignationPreviewEvidenceSchema,
});
//# sourceMappingURL=allocation-resignation-preview.js.map